import type React from "react";
import type { Post } from "@/types/channel/post";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CommentForm } from "../CommentForm/CommentForm";
import { CommentList } from "../CommentList/CommentList";
import { formatDistance } from "date-fns";
import { ja } from "date-fns/locale";
import { MoreHorizontal, MessageSquare, Heart } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface PostItemProps {
	post: Post;
}

export const PostItem: React.FC<PostItemProps> = ({ post }) => {
	const { user } = useAuth();
	const [showComments, setShowComments] = useState(false);

	return (
		<div className="bg-white rounded-lg shadow-sm p-6">
			<div className="flex justify-between">
				<div className="flex space-x-3">
					<Avatar>
						<AvatarImage src={post.user?.avatarUrl} />
						<AvatarFallback>{post.user?.username?.[0]}</AvatarFallback>
					</Avatar>
					<div>
						<p className="font-semibold">{post.user?.username}</p>
						<p className="text-sm text-gray-500">
							{formatDistance(new Date(post.createdAt), new Date(), {
								addSuffix: true,
								locale: ja,
							})}
						</p>
					</div>
				</div>

				{user?.id === post.userId && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="sm">
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuItem>編集</DropdownMenuItem>
							<DropdownMenuItem className="text-red-600">削除</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>

			<p className="mt-3">{post.content}</p>

			<div className="mt-4 flex items-center space-x-4">
				<Button variant="ghost" size="sm">
					<Heart className="h-4 w-4 mr-1" />
					{post.reactions?.length || 0}
				</Button>
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setShowComments(!showComments)}
				>
					<MessageSquare className="h-4 w-4 mr-1" />
					{post.comments?.length || 0}
				</Button>
			</div>

			{showComments && (
				<div className="mt-4 space-y-4">
					<CommentForm postId={post.id} />
					<CommentList comments={post.comments || []} />
				</div>
			)}
		</div>
	);
};
